import { GridColDef, GridColumnVisibilityModel } from '@mui/x-data-grid'
import { PreviewNameCell } from '@scoir/data-table'

import ScoirBadge from 'components/ScoirBadge/ScoirBadge'

import SchoolTypePill from '../components/SchoolTypePill'
import TargetIcon from '../components/TargetIcon'
import { UserDisplay } from '../components/Views/types'
import VisitAvailabilityLink from '../components/VisitAvailabilityLink'
import { TITLE_1_PROGRAM_OPTIONS } from '../title-one'
import CellData from './CellData'
import ColumnHeader from './HandraisersColumnHeader'
import HistoricFollowersColumnHeader from './HistoricFollowersColumnHeader'
import {
  CLASSES,
  Class,
  HISTORIC_FOLLOWER_YEARS,
  HistoricFollowerYear,
  sumByClasses,
  sumHistoricFollowersByYears,
} from './gradYears'
import { ExploreHighSchool, isValidSortKey } from './types'
import { ToggleTargeted } from './useTargetHighschoolsExplore'

interface ExploreTableColumns {
  onClickPreview: (highschoolId: string | null) => void
  userDisplay?: UserDisplay
  toggleTargeted: ToggleTargeted
  selectedHandraiserClasses?: Class[]
  onChangeHandraiserClasses?: (next: Class[]) => void
  selectedFollowerClasses?: Class[]
  onChangeFollowerClasses?: (next: Class[]) => void
  selectedHistoricFollowerYears?: HistoricFollowerYear[]
  onChangeHistoricFollowerYears?: (next: HistoricFollowerYear[]) => void
  currentSeniorYear: number
  showPremiumColumns?: boolean
}

const PREMIUM_ENGAGEMENT_COLUMNS = new Set(['target'])

export interface ExtendedGridColDef extends GridColDef<ExploreHighSchool> {
  isVisible: boolean
  section: string
  hideable?: boolean
  longHeaderName: string
  defaultSortIndex: number
  skipExport?: boolean
  headerName: string
}

const enforceSortable = (def: ExtendedGridColDef) => ({
  ...def,
  sortable: isValidSortKey(def.field),
})

export const columnSectionOrderModel = [
  'Visits',
  'School Details',
  'Location',
  'Student Demographics',
  'Student Engagement',
  'Post-Secondary Enrollment',
]

const getColumns = ({
  onClickPreview,
  userDisplay,
  toggleTargeted,
  selectedHandraiserClasses = [...CLASSES],
  onChangeHandraiserClasses,
  selectedFollowerClasses = [...CLASSES],
  onChangeFollowerClasses,
  selectedHistoricFollowerYears = [...HISTORIC_FOLLOWER_YEARS],
  onChangeHistoricFollowerYears,
  currentSeniorYear,
  showPremiumColumns = false,
}: ExploreTableColumns) => {
  const columns: ExtendedGridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      longHeaderName: 'School Name',
      section: 'School Details',
      hideable: false,
      width: userDisplay?.widthModel?.name ?? 300,
      isVisible: true,
      defaultSortIndex: 0,
      renderCell: (params) => (
        <PreviewNameCell
          text="View"
          name={params.row.name}
          setPreview={() => {
            onClickPreview(params.row.id)
          }}
        />
      ),
    },
    {
      field: 'target',
      headerName: 'Targets',
      longHeaderName: 'Target Schools',
      section: 'School Details',
      hideable: false,
      skipExport: true,
      width: userDisplay?.widthModel?.target ?? 100,
      isVisible: userDisplay?.visibilityModel?.target ?? false,
      defaultSortIndex: 50,
      align: 'center',
      renderCell: (params) => {
        return (
          <TargetIcon
            hsId={params.row.id}
            targeted={params.row.target ?? false}
            toggleTargeted={toggleTargeted}
          />
        )
      },
    },
    {
      field: 'ceebCode',
      headerName: 'CEEB Code',
      longHeaderName: 'CEEB Code',
      section: 'School Details',
      width: userDisplay?.widthModel?.ceebCode ?? 100,
      isVisible: userDisplay?.visibilityModel?.ceebCode ?? false,
      defaultSortIndex: 100,
      renderCell: (params) => {
        return <CellData>{params.row.ceebCode}</CellData>
      },
    },
    {
      field: 'state',
      headerName: 'State',
      longHeaderName: 'State',
      section: 'Location',
      width: userDisplay?.widthModel?.state ?? 100,
      isVisible: userDisplay?.visibilityModel?.state ?? false,
      defaultSortIndex: 200,
      renderCell: (params) => {
        return <CellData>{params.row.state}</CellData>
      },
    },
    {
      field: 'city',
      headerName: 'City',
      longHeaderName: 'City',
      section: 'Location',
      width: userDisplay?.widthModel?.city ?? 150,
      isVisible: userDisplay?.visibilityModel?.city ?? false,
      defaultSortIndex: 300,
    },
    {
      field: 'zipCode',
      headerName: 'Zip Code',
      longHeaderName: 'Zip Code',
      section: 'Location',
      width: userDisplay?.widthModel?.zipCode ?? 150,
      isVisible: userDisplay?.visibilityModel?.zipCode ?? false,
      defaultSortIndex: 400,
    },
    {
      field: 'schoolType',
      headerName: 'Type',
      longHeaderName: 'School Type',
      section: 'School Details',
      width: userDisplay?.widthModel?.schoolType ?? 90,
      isVisible: userDisplay?.visibilityModel?.schoolType ?? false,
      defaultSortIndex: 500,
      renderCell: (params) => {
        return (
          <CellData>
            {params.row.schoolType ? <SchoolTypePill schoolType={params.row.schoolType} /> : undefined}
          </CellData>
        )
      },
    },
    {
      field: 'isActive',
      headerName: 'On Scoir',
      longHeaderName: 'Active on Scoir',
      section: 'School Details',
      width: userDisplay?.widthModel?.isActive ?? 120,
      isVisible: userDisplay?.visibilityModel?.isActive ?? false,
      defaultSortIndex: 600,
      renderCell: (params) => (params.row.isActive ? <ScoirBadge /> : ''),
    },
    {
      field: 'nextVisitDate',
      headerName: 'Next Available Visit',
      longHeaderName: 'Next Available Visit Date',
      section: 'Visits',
      width: userDisplay?.widthModel?.nextVisitDate ?? 250,
      isVisible: userDisplay?.visibilityModel?.nextVisitDate ?? false,
      defaultSortIndex: 700,
      renderCell: (params) => {
        return (
          <CellData>
            {params.row.nextVisitDate ? (
              <VisitAvailabilityLink
                highSchool={{
                  id: params.row.id,
                  name: params.row.name,
                  nextVisitDate: params.row.nextVisitDate,
                }}
              />
            ) : undefined}
          </CellData>
        )
      },
    },
    {
      field: 'numHandraisers',
      headerName: 'Handraisers',
      longHeaderName: 'Current Handraisers',
      section: 'Student Engagement',
      width: userDisplay?.widthModel?.numHandraisers ?? 200,
      isVisible: userDisplay?.visibilityModel?.numHandraisers ?? false,
      defaultSortIndex: 850,
      renderHeader: () =>
        onChangeHandraiserClasses ? (
          <ColumnHeader
            title="Handraisers"
            selectedClasses={selectedHandraiserClasses}
            onChange={onChangeHandraiserClasses}
          />
        ) : (
          <span>Handraisers</span>
        ),
      renderCell: (params) => {
        const value = sumByClasses(params.row.numHandraisersByGradYear, selectedHandraiserClasses, currentSeniorYear)
        return <CellData>{value}</CellData>
      },
    },
    {
      field: 'numFollowers',
      headerName: 'Followers',
      longHeaderName: 'Current Followers',
      section: 'Student Engagement',
      width: userDisplay?.widthModel?.numFollowers ?? 200,
      isVisible: userDisplay?.visibilityModel?.numFollowers ?? false,
      defaultSortIndex: 860,
      renderHeader: () =>
        onChangeFollowerClasses ? (
          <ColumnHeader
            title="Followers"
            selectedClasses={selectedFollowerClasses}
            onChange={onChangeFollowerClasses}
          />
        ) : (
          <span>Followers</span>
        ),
      renderCell: (params) => {
        const value = sumByClasses(params.row.numFollowersByGradYear, selectedFollowerClasses, currentSeniorYear)
        return <CellData>{value}</CellData>
      },
    },
    {
      field: 'numHistoricFollowers',
      headerName: 'Historic Followers',
      longHeaderName: 'Historic Followers',
      section: 'Student Engagement',
      width: userDisplay?.widthModel?.numHistoricFollowers ?? 220,
      isVisible: userDisplay?.visibilityModel?.numHistoricFollowers ?? false,
      defaultSortIndex: 870,
      renderHeader: () =>
        onChangeHistoricFollowerYears ? (
          <HistoricFollowersColumnHeader
            title="Historic Followers"
            selectedYears={selectedHistoricFollowerYears}
            onChange={onChangeHistoricFollowerYears}
          />
        ) : (
          <span>Historic Followers</span>
        ),
      renderCell: (params) => {
        const value = sumHistoricFollowersByYears(
          params.row.numHistoricFollowersByGradYear,
          selectedHistoricFollowerYears,
          currentSeniorYear,
        )
        return <CellData>{value}</CellData>
      },
    },
    {
      field: 'hasCounselorSuggestions',
      headerName: 'Promoter School',
      longHeaderName: 'Promoter School',
      section: 'Student Engagement',
      width: userDisplay?.widthModel?.hasCounselorSuggestions ?? 200,
      isVisible: userDisplay?.visibilityModel?.hasCounselorSuggestions ?? false,
      defaultSortIndex: 1000,
      valueGetter: (params) => {
        if (params.row.hasCounselorSuggestions === undefined) return undefined
        return params.row.hasCounselorSuggestions ? 'Yes' : 'No'
      },
      renderCell: (params) => {
        return <CellData>{params.value}</CellData>
      },
    },
    {
      field: 'isTitle1Eligible',
      headerName: 'Title 1 Eligible',
      longHeaderName: 'Title 1 Program Eligibility',
      section: 'School Details',
      width: userDisplay?.widthModel?.isTitle1Eligible ?? 150,
      isVisible: userDisplay?.visibilityModel?.isTitle1Eligible ?? false,
      defaultSortIndex: 1100,
      renderCell: (params) => {
        const val = params.row.isTitle1Eligible
        return <CellData>{val === undefined ? undefined : val ? 'Yes' : 'No'}</CellData>
      },
    },
    {
      field: 'religiousAffiliation',
      headerName: 'Religious Affiliation',
      longHeaderName: 'Religious Affiliation',
      section: 'School Details',
      width: userDisplay?.widthModel?.religiousAffiliation ?? 200,
      isVisible: userDisplay?.visibilityModel?.religiousAffiliation ?? false,
      defaultSortIndex: 1200,
      renderCell: (params) => {
        return <CellData>{params.row.religiousAffiliation}</CellData>
      },
    },
    {
      field: 'locale',
      headerName: 'Locale',
      longHeaderName: 'School Locale',
      section: 'Location',
      width: userDisplay?.widthModel?.locale ?? 150,
      isVisible: userDisplay?.visibilityModel?.locale ?? false,
      defaultSortIndex: 1300,
      renderCell: (params) => {
        return <CellData>{params.row.locale}</CellData>
      },
    },
    {
      field: 'geomarket',
      headerName: 'Geomarket',
      longHeaderName: 'Geographic Market',
      section: 'Location',
      width: userDisplay?.widthModel?.geomarket ?? 200,
      isVisible: userDisplay?.visibilityModel?.geomarket ?? false,
      defaultSortIndex: 1400,
      renderCell: (params) => {
        return <CellData>{params.row.geomarket ?? undefined}</CellData>
      },
    },
    {
      field: 'seniorClassSize',
      headerName: 'Senior Class',
      longHeaderName: 'Senior Class Size',
      section: 'Student Demographics',
      width: userDisplay?.widthModel?.seniorClassSize ?? 150,
      isVisible: userDisplay?.visibilityModel?.seniorClassSize ?? false,
      defaultSortIndex: 1500,
    },
    {
      field: 'title1Program',
      headerName: 'Title 1 Program',
      longHeaderName: 'Title 1 Program Type',
      section: 'School Details',
      width: userDisplay?.widthModel?.title1Program ?? 200,
      isVisible: userDisplay?.visibilityModel?.title1Program ?? false,
      defaultSortIndex: 1600,
      renderCell: (params) => {
        const val = params.row.title1Program
        return <CellData>{val ? TITLE_1_PROGRAM_OPTIONS[val].label : undefined}</CellData>
      },
    },
    {
      field: 'pctFreeReducedLunch',
      headerName: '% Free or Reduced Lunch',
      longHeaderName: '% Free or Reduced Lunch',
      section: 'Student Demographics',
      width: userDisplay?.widthModel?.pctFreeReducedLunch ?? 210,
      isVisible: userDisplay?.visibilityModel?.pctFreeReducedLunch ?? false,
      defaultSortIndex: 1700,
      renderCell: (params) => {
        return (
          <CellData>
            {params.row.pctFreeReducedLunch !== undefined ? percentFormat(params.row.pctFreeReducedLunch) : undefined}
          </CellData>
        )
      },
    },
    {
      field: 'pctFirstGenerationStudents',
      headerName: '% First Generation',
      longHeaderName: '% First Generation Students',
      section: 'School Details',
      width: userDisplay?.widthModel?.pctFirstGenerationStudents ?? 210,
      isVisible: userDisplay?.visibilityModel?.pctFirstGenerationStudents ?? false,
      defaultSortIndex: 1800,
      renderCell: (params) => {
        return (
          <CellData>
            {params.row.pctFirstGenerationStudents !== undefined
              ? percentFormat(params.row.pctFirstGenerationStudents, false)
              : undefined}
          </CellData>
        )
      },
    },
    {
      field: 'pctAttendFourYearCollegeStudents',
      headerName: '% to 4-Year',
      longHeaderName: '% of Students Attending 4-Year Colleges',
      section: 'Post-Secondary Enrollment',
      width: userDisplay?.widthModel?.pctAttendFourYearCollegeStudents ?? 210,
      isVisible: userDisplay?.visibilityModel?.pctAttendFourYearCollegeStudents ?? false,
      defaultSortIndex: 1900,
      renderCell: (params) => {
        return (
          <CellData>
            {params.row.pctAttendFourYearCollegeStudents !== undefined
              ? percentFormat(params.row.pctAttendFourYearCollegeStudents, false)
              : undefined}
          </CellData>
        )
      },
    },
    {
      field: 'pctAttendTwoYearCollegeStudents',
      headerName: '% to 2-Year',
      longHeaderName: '% of Students Attending 2-Year Colleges',
      section: 'Post-Secondary Enrollment',
      width: userDisplay?.widthModel?.pctAttendTwoYearCollegeStudents ?? 210,
      isVisible: userDisplay?.visibilityModel?.pctAttendTwoYearCollegeStudents ?? false,
      defaultSortIndex: 2000,
      renderCell: (params) => {
        return (
          <CellData>
            {params.row.pctAttendTwoYearCollegeStudents !== undefined
              ? percentFormat(params.row.pctAttendTwoYearCollegeStudents, false)
              : undefined}
          </CellData>
        )
      },
    },
    {
      field: 'pctAsianStudents',
      headerName: '% Asian',
      longHeaderName: '% of Asian Students',
      section: 'Student Demographics',
      width: userDisplay?.widthModel?.pctAsianStudents ?? 210,
      isVisible: userDisplay?.visibilityModel?.pctAsianStudents ?? false,
      defaultSortIndex: 2100,
      renderCell: (params) => {
        return (
          <CellData>
            {params.row.pctAsianStudents !== undefined ? percentFormat(params.row.pctAsianStudents, false) : undefined}
          </CellData>
        )
      },
    },
    {
      field: 'pctBlackAfricanAmericanStudents',
      headerName: '% Black/African American',
      longHeaderName: '% of Black/African American Students',
      section: 'Student Demographics',
      width: userDisplay?.widthModel?.pctBlackAfricanAmericanStudents ?? 210,
      isVisible: userDisplay?.visibilityModel?.pctBlackAfricanAmericanStudents ?? false,
      defaultSortIndex: 2200,
      renderCell: (params) => {
        return (
          <CellData>
            {params.row.pctBlackAfricanAmericanStudents !== undefined
              ? percentFormat(params.row.pctBlackAfricanAmericanStudents, false)
              : undefined}
          </CellData>
        )
      },
    },
    {
      field: 'pctHispanicLatinoStudents',
      headerName: '% Hispanic/Latino',
      longHeaderName: '% of Hispanic/Latino Students',
      section: 'Student Demographics',
      width: userDisplay?.widthModel?.pctHispanicLatinoStudents ?? 210,
      isVisible: userDisplay?.visibilityModel?.pctHispanicLatinoStudents ?? false,
      defaultSortIndex: 2300,
      renderCell: (params) => {
        return (
          <CellData>
            {params.row.pctHispanicLatinoStudents !== undefined
              ? percentFormat(params.row.pctHispanicLatinoStudents, false)
              : undefined}
          </CellData>
        )
      },
    },
    {
      field: 'pctNativeAmericanStudents',
      headerName: '% Native American',
      longHeaderName: '% of Native American Students',
      section: 'Student Demographics',
      width: userDisplay?.widthModel?.pctNativeAmericanStudents ?? 210,
      isVisible: userDisplay?.visibilityModel?.pctNativeAmericanStudents ?? false,
      defaultSortIndex: 2400,
      renderCell: (params) => {
        return (
          <CellData>
            {params.row.pctNativeAmericanStudents !== undefined
              ? percentFormat(params.row.pctNativeAmericanStudents, false)
              : undefined}
          </CellData>
        )
      },
    },
    {
      field: 'pctWhiteCaucasianStudents',
      headerName: '% White/Caucasian',
      longHeaderName: '% of White/Caucasian Students',
      section: 'Student Demographics',
      width: userDisplay?.widthModel?.pctWhiteCaucasianStudents ?? 210,
      isVisible: userDisplay?.visibilityModel?.pctWhiteCaucasianStudents ?? false,
      defaultSortIndex: 2500,
      renderCell: (params) => {
        return (
          <CellData>
            {params.row.pctWhiteCaucasianStudents !== undefined
              ? percentFormat(params.row.pctWhiteCaucasianStudents, false)
              : undefined}
          </CellData>
        )
      },
    },
  ]
  const visibleColumns = columns.filter((c) => showPremiumColumns || !PREMIUM_ENGAGEMENT_COLUMNS.has(c.field))

  const sortedColumns = visibleColumns.map(enforceSortable)
  sortedColumns.sort((a, b) => {
    const aOrder = userDisplay?.orderModel?.[a.field] ?? 0
    const bOrder = userDisplay?.orderModel?.[b.field] ?? 0
    return aOrder - bOrder
  })

  return sortedColumns
}

export function percentFormat(num?: number, isDecimal: boolean = true) {
  let numToFormat = num
  if (!isDecimal && numToFormat) {
    numToFormat = numToFormat / 100
  }
  if (!numToFormat || numToFormat === 0) {
    return '0%' // NOTE: Shouldn't happen based on usage, but just in case.
  }

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'percent',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })

  return formatter.format(numToFormat)
}

export const premiumColumns = [
  'numHandraisers',
  'numFollowers',
  'numHistoricFollowers',
  'hasCounselorSuggestions',
  'isTitle1Eligible',
  'pctOtherUndisclosedStudents',
  'locale',
  'geomarket',
  'seniorClassSize',
  'title1Program',
  'pctFreeReducedLunch',
  'pctFirstGenerationStudents',
  'pctAttendFourYearCollegeStudents',
  'pctAttendTwoYearCollegeStudents',
  'pctWhiteCaucasianStudents',
  'pctBlackAfricanAmericanStudents',
  'pctHispanicLatinoStudents',
  'pctAsianStudents',
  'pctNativeAmericanStudents',
]

export const getColumnVisibility = (
  visibilityModel: GridColumnVisibilityModel = {},
  isPremiumExplorerEnabled: boolean,
) => {
  premiumColumns.forEach((column) => {
    if (!isPremiumExplorerEnabled) {
      visibilityModel[column] = false
    }
  })

  return visibilityModel
}

export default getColumns
